import { useEffect, useState } from "react";
import { useT } from "@/lib/i18n";

const STORAGE_KEY = "hvi-cookie-consent-v1";

export type ConsentState = "accepted" | "rejected" | null;

export function getConsent(): ConsentState {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "accepted" || v === "rejected" ? v : null;
}

export default function CookieConsent() {
  const { t } = useT();
  const [consent, setConsent] = useState<ConsentState>(null);
  const [mounted, setMounted] = useState(false);
  const [forceOpen, setForceOpen] = useState(false);

  useEffect(() => {
    setMounted(true);
    setConsent(getConsent());
    const open = () => setForceOpen(true);
    window.addEventListener("hvi-consent-open", open);
    return () => window.removeEventListener("hvi-consent-open", open);
  }, []);

  const decide = (value: "accepted" | "rejected") => {
    window.localStorage.setItem(STORAGE_KEY, value);
    setConsent(value);
    setForceOpen(false);
    window.dispatchEvent(new CustomEvent("hvi-consent-change", { detail: value }));
  };

  if (!mounted) return null;
  if (consent !== null && !forceOpen) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed bottom-0 inset-x-0 z-[1000] border-t border-border bg-[var(--hvi-surface-container-high)]/95 backdrop-blur supports-[backdrop-filter]:bg-[var(--hvi-surface-container-high)]/80"
    >
      <div className="max-w-5xl mx-auto px-4 py-3 flex flex-col md:flex-row items-start md:items-center gap-3 text-xs md:text-sm">
        <p className="flex-1 text-hvi-on-surface-variant leading-relaxed">
          {t("cookie.text")}{" "}
          <a href="/resources#privacy" className="underline text-hvi-primary hover:opacity-80">
            {t("cookie.link")}
          </a>
          .
        </p>
        <div className="flex gap-2 self-end md:self-auto">
          <button
            onClick={() => decide("rejected")}
            className="border border-border px-3 py-1.5 text-[11px] uppercase tracking-wider text-hvi-on-surface-variant hover:text-foreground hover:border-foreground/40 transition"
          >
            {t("cookie.reject")}
          </button>
          <button
            onClick={() => decide("accepted")}
            className="border border-hvi-primary bg-hvi-primary/10 px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold text-hvi-primary hover:bg-hvi-primary/20 transition"
          >
            {t("cookie.accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
